import React from 'react'
import { css } from '@emotion/core'
import { useQuery } from '@apollo/client'
import { useLocation } from '@fs/zion-router'
import { Paragraph, colors, Grid, H1, Overline, Subheading } from '@fs/zion-ui'
import { BrightspotSharedModulesContent } from '@fs/zion-cms'
import Map, { Marker } from '@fs/zion-map'
import './leaflet.css'
import QUERY from './StatePage.gql'

export default function StatePage() {
  const { pathname } = useLocation()
  const pathParts = pathname.split('/')
  const stateCode = pathParts[pathParts.length - 1]
  const { loading, error, data } = useQuery(QUERY, {
    variables: { path: `/${stateCode}` },
    context: { apiName: 'zion-cms-beta:custom' },
  })

  if (loading) {
    return <div data-testid="loading-state-page">Loading...</div>
  }
  if (error) {
    return <div data-testid="error-state-page">Error! ${JSON.stringify(error)}</div>
  }

  return <StatePageContent data={data} />
}

export function StatePageContent({ data }) {
  if (!data) {
    return <div data-testid="no-data-state-page">No data returned</div>
  }

  let bgColor = colors.blue02
  if (data?.StateDemo?.bannerColor) {
    bgColor = colors[`${data.StateDemo.bannerColor}05`]
  }

  return (
    <>
      <div
        css={css`
          text-align: center;
          background: ${bgColor || colors.blue}
          padding: 10px;
        `}
        data-testid="state-content"
      >
        <H1 size="H3" background={colors.green}>
          <Overline>{`${data?.StateDemo?.code || 'N/A'}`}</Overline>
          {data?.StateDemo?.name || 'Unknown'}
          <Subheading>State Details</Subheading>
        </H1>
        <hr />
        <Grid>
          <StateFlag columns={3} name={data?.StateDemo?.name} url={data?.StateDemo?.stateFlag?.publicUrl} />
          <StateIcon columns={2} title="State Bird" value={data?.StateDemo?.stateBird} />
          <StateIcon columns={2} title="State Flower" value={data?.StateDemo?.stateFlower} />
          <StateIcon columns={2} title="State Tree" value={data?.StateDemo?.stateTree} />
          <StateFlag columns={3} name={data?.StateDemo?.name} url={data?.StateDemo?.stateFlag?.publicUrl} />
        </Grid>
      </div>
      {!data.StateDemo?.location && <Paragraph size="sm">No Location Specified</Paragraph>}
      {data.StateDemo?.location && (
        <div
          css={css`
            margin-top: 20px;
            height: 400px;
            width: 100%;
          `}
        >
          <Map
            bing
            initialLatitude={data.StateDemo?.location?.x}
            initialLongitude={data.StateDemo?.location?.y}
            initialZoom={5}
          >
            <Marker
              latitude={data.StateDemo?.location?.x}
              longitude={data.StateDemo?.location?.y}
              label={data.StateDemo?.name}
              ariaLabel={data.StateDemo?.name}
            />
          </Map>
        </div>
      )}
      {/* eslint-disable-next-line no-underscore-dangle */}
      {data.StateDemo?.sharedReference?._id && (
        <div
          css={css`
            width: 90%;
            padding: 10px;
            margin-top: 30px;
            margin-inline-start: 20px;
          `}
        >
          <BrightspotSharedModulesContent
            /* eslint-disable-next-line no-underscore-dangle */
            url={data.StateDemo?.sharedReference?._id}
            site="zion-cms-beta"
            fallback={<div>Error retrieving shared content</div>}
          />
        </div>
      )}
      {/* eslint-disable-next-line no-underscore-dangle */}
      {!data.StateDemo?.sharedReference?._id && (
        <h4
          css={css`
            text-align: center;
            padding: 10px;
          `}
        >
          No Shared Reference
        </h4>
      )}
    </>
  )
}

function StateFlag({ name = 'Unknown state name', url }) {
  if (!url) {
    return <Paragraph size="sm">No Flag</Paragraph>
  }
  return (
    <img
      css={css`
        width: 300px;
        margin: auto;
      `}
      alt={`State flag for ${name || 'Unknown state name'}`}
      src={url}
    />
  )
}

function StateIcon({ title = 'Unknown icon title', value = 'Unknown icon value' }) {
  return (
    <span
      css={css`
        border: 1px solid black;
        height: 100%;
        background: white;
      `}
    >
      <H1 size="H5">
        {title}
        <Subheading>{value}</Subheading>
      </H1>
    </span>
  )
}
